// src/components/grammarQuest/WheelSelector.jsx
// Selector tipo rueda/carrusel 3D — soporte: touch, mouse, teclado
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const ITEM_HEIGHT = 48; // px — altura compacta para pantallas reducidas

export default function WheelSelector({ options = [], selectedIndex, onChange, disabled }) {
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // ─── Teclado ───
  useEffect(() => {
    const handleKey = (e) => {
      if (disabled) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        onChange(Math.max(0, selectedIndex - 1));
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        onChange(Math.min(options.length - 1, selectedIndex + 1));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, options.length, onChange, disabled]);

  // ─── Calcular estilo 3D para cada ítem ───
  const getItemStyle = (index) => {
    const diff = index - selectedIndex;
    const absDiff = Math.abs(diff);

    if (absDiff > 2) return { opacity: 0, pointerEvents: 'none' };

    const rotateX = diff * -20; 
    const translateY = diff * ITEM_HEIGHT;
    const scale = absDiff === 0 ? 1 : absDiff === 1 ? 0.85 : 0.65;
    const opacity = absDiff === 0 ? 1 : absDiff === 1 ? 0.4 : 0.15;
    const zIndex = 10 - absDiff;

    return {
      transform: `perspective(400px) rotateX(${rotateX}deg) translateY(${translateY}px) scale(${scale})`,
      opacity,
      zIndex,
      transition: isDragging ? 'none' : 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
    };
  };

  // ─── Touch / Mouse Drag ───
  const handlePointerDown = useCallback((e) => {
    if (disabled) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart(y);
    setIsDragging(true);
  }, [disabled]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging || dragStart === null || disabled) return;
    if (e.cancelable && e.touches) {
      e.preventDefault();
    }
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = dragStart - y;
    if (Math.abs(delta) > 16) {
      const direction = delta > 0 ? 1 : -1;
      const next = Math.max(0, Math.min(options.length - 1, selectedIndex + direction));
      onChange(next);
      setDragStart(y);
    }
  }, [isDragging, dragStart, selectedIndex, options.length, onChange, disabled]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  return (
    <div className="flex flex-col items-center gap-1.5 select-none w-full max-w-md mx-auto">
      
      {/* Contenedor Principal (Estilo Pizarra/Azul) */}
      <div className="relative w-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-3 shadow-xl border border-slate-700/80 overflow-hidden flex flex-col items-center">
        
        {/* Flecha ARRIBA */}
        <button
          onClick={() => !disabled && onChange(Math.max(0, selectedIndex - 1))}
          disabled={disabled || selectedIndex === 0}
          className="relative z-20 p-1 mb-0.5 rounded-full text-slate-400 hover:text-white hover:bg-slate-700/60 disabled:opacity-30 disabled:hover:bg-transparent transition-all active:scale-95 cursor-pointer"
          aria-label="Opción anterior"
        >
          <ChevronUp size={22} />
        </button>

        {/* RUEDA — touchAction: none para evitar que arrastre la página al deslizar */}
        <div
          ref={containerRef}
          className="relative overflow-hidden w-full select-none"
          style={{ height: `${ITEM_HEIGHT * 3}px`, touchAction: 'none' }}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          role="listbox"
          aria-label="Selector de respuesta"
        >
          {/* Sombras de profundidad tipo cilindro */}
          <div className="absolute inset-x-0 top-0 h-12 z-20 pointer-events-none bg-gradient-to-b from-slate-950/90 via-slate-950/50 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-12 z-20 pointer-events-none bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-transparent" />

          {/* Ítems */}
          <div className="absolute inset-x-0" style={{ top: `${ITEM_HEIGHT}px` }}>
            {options.map((option, index) => {
              const isCurrent = index === selectedIndex;
              return (
                <div
                  key={index}
                  role="option"
                  aria-selected={isCurrent}
                  className="absolute top-0 w-full flex items-center justify-center cursor-pointer px-4"
                  style={{ ...getItemStyle(index), height: `${ITEM_HEIGHT}px` }}
                  onClick={() => !disabled && onChange(index)}
                >
                  <div className={`
                    w-full py-2 px-3 rounded-xl text-center font-medium transition-all duration-200 flex items-center justify-center gap-2
                    ${isCurrent
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/40 border border-blue-400 ring-2 ring-blue-400/50 text-base font-bold'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700/40 text-sm'
                    }
                  `}>
                    {isCurrent && (
                      <span className="text-yellow-300 text-xs font-bold uppercase tracking-wider animate-pulse">►</span>
                    )}
                    <span>{option}</span>
                    {isCurrent && (
                      <span className="text-yellow-300 text-xs font-bold uppercase tracking-wider animate-pulse">◄</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Flecha ABAJO */}
        <button
          onClick={() => !disabled && onChange(Math.min(options.length - 1, selectedIndex + 1))}
          disabled={disabled || selectedIndex === options.length - 1}
          className="relative z-20 p-1 mt-0.5 rounded-full text-slate-400 hover:text-white hover:bg-slate-700/60 disabled:opacity-30 disabled:hover:bg-transparent transition-all active:scale-95 cursor-pointer"
          aria-label="Opción siguiente"
        >
          <ChevronDown size={22} />
        </button>
      </div>

      {/* Indicador de posición — dots */}
      <div className="flex gap-1.5 mt-1">
        {options.map((_, i) => (
          <button
            key={i}
            onClick={() => !disabled && onChange(i)}
            className={`rounded-full transition-all duration-300 ${
              i === selectedIndex
                ? 'w-5 h-1.5 bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.6)]'
                : 'w-1.5 h-1.5 bg-slate-400 hover:bg-slate-300'
            }`}
            aria-label={`Seleccionar opción ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
